/**
 * Node visual para Filtrar Array
 * Exibe configuração de filtro de array no canvas
 * Suporta múltiplas condições com operadores E/OU
 * 
 * RELEASE: 1.0.0 - 2024-12-24 - Novo node para filtrar arrays de webhooks
 * RELEASE: 2.0.0 - 2024-12-30 - Suporte a múltiplas condições com E/OU
 */

import { memo, useMemo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Filter } from 'lucide-react';
import { FlowNodeToolbar } from './FlowNodeToolbar';
import { FilterArrayCondition } from '@/types/flows';

interface FilterArrayNodeProps {
  data: {
    type: string;
    label: string;
    config?: {
      source_variable?: string;
      output_variable?: string;
      // Novo formato: múltiplas condições
      conditions?: FilterArrayCondition[];
      logicOperator?: 'and' | 'or';
      // Legado: condição única
      field_path?: string;
      operator?: string;
      filter_value?: string;
    };
    onEdit?: () => void;
    onDelete?: () => void;
  };
  id: string;
  selected?: boolean;
}

/**
 * Mapeamento de operadores para exibição amigável
 */
const operatorLabels: Record<string, string> = {
  equals: '=',
  not_equals: '≠',
  contains: 'contém',
  not_contains: 'não contém',
  starts_with: 'inicia com',
  ends_with: 'termina com',
  greater: '>',
  less: '<',
  greater_equal: '>=',
  less_equal: '<=',
  is_set: 'definido',
  is_empty: 'vazio',
};

/**
 * Componente visual do node Filter Array
 */
export const FilterArrayNode = memo(({ data, id, selected }: FilterArrayNodeProps) => {
  const config = data.config || {};
  const sourceVariable = config.source_variable || '';
  const outputVariable = config.output_variable || '';

  // Callbacks injetados via FlowCanvas
  const onEdit = data.onEdit;
  const onDuplicate = (data as any).onDuplicate;
  const onDelete = data.onDelete;
  const onTestFromHere = (data as any).onTestFromHere;

  /**
   * Normaliza condições para suportar formato legado e novo
   */
  const conditions = useMemo(() => {
    // Formato novo: múltiplas condições
    if (config.conditions && Array.isArray(config.conditions) && config.conditions.length > 0) {
      return config.conditions;
    }
    // Formato legado: converte para array
    if (config.field_path) {
      return [{
        id: 'legacy-1',
        field_path: config.field_path,
        operator: config.operator || 'equals',
        value: config.filter_value || '',
      }];
    }
    return [];
  }, [config]);

  const logicOperator = config.logicOperator || 'and';

  // Verifica se está configurado
  const isConfigured = sourceVariable && outputVariable && conditions.length > 0 && conditions[0].field_path;

  // Limite de condições a exibir no node
  const MAX_VISIBLE_CONDITIONS = 3;
  const visibleConditions = conditions.slice(0, MAX_VISIBLE_CONDITIONS);
  const hiddenCount = conditions.length - MAX_VISIBLE_CONDITIONS;

  return (
    <div className="px-4 py-3 shadow-lg rounded-lg border-2 border-amber-500 bg-card min-w-[220px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={data.type || 'filter_array'}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-amber-500" />
      
      <div className="flex items-start gap-2">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-500 text-white flex-shrink-0">
          <Filter className="h-4 w-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm mb-1">{(data as any).config?.nickname || 'Filtrar Array'}</div>
          
          {isConfigured ? (
            <div className="text-xs space-y-1">
              {/* Entrada */}
              <div className="font-mono bg-muted px-2 py-1 rounded">
                <span className="text-muted-foreground">De: </span>
                <span className="text-amber-600 dark:text-amber-400 font-semibold">
                  {sourceVariable}
                </span>
              </div>
              
              {/* Condições */}
              {visibleConditions.map((cond, idx) => (
                <div key={cond.id || idx}>
                  {/* Operador lógico entre condições */}
                  {idx > 0 && (
                    <div className="text-center py-0.5">
                      <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                        {logicOperator === 'and' ? 'E' : 'OU'}
                      </span>
                    </div>
                  )}
                  
                  {/* Condição */}
                  <div className="font-mono bg-muted px-2 py-1 rounded">
                    <span className="text-blue-600 dark:text-blue-400">{cond.field_path}</span>
                    {' '}
                    <span className="text-muted-foreground">
                      {operatorLabels[cond.operator] || cond.operator}
                    </span>
                    {' '}
                    {cond.operator !== 'is_set' && cond.operator !== 'is_empty' && (
                      <span className="text-green-600 dark:text-green-400">"{cond.value}"</span>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Indicador de condições ocultas */}
              {hiddenCount > 0 && (
                <div className="text-center">
                  <span className="text-[10px] text-muted-foreground">
                    (+{hiddenCount} {hiddenCount === 1 ? 'condição' : 'condições'})
                  </span>
                </div>
              )}
              
              {/* Saída */}
              <div className="font-mono bg-muted px-2 py-1 rounded">
                <span className="text-muted-foreground">Salvar em: </span>
                <span className="text-cyan-600 dark:text-cyan-400 font-semibold">
                  {outputVariable}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">
              Clique para configurar
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-amber-500" />
    </div>
  );
});

FilterArrayNode.displayName = 'FilterArrayNode';
